/**
 * Puts a finished candidate on the recruiter's queue [KPW-2].
 *
 * **Fire-and-forget, like the I-9 notice and for the same reason.** A recruiter's list being unreachable is
 * a staff problem; holding somebody at an unattended kiosk over it is the dead end BR-K7 and decision 0046
 * forbid. What comes back is never shown as an error: whether the write LANDED, and the name the entry carries
 * when the api says it (see `handOffToRecruiter`). The one use of `landed` is the store's: a write that did not
 * land re-opens the session's help controls to the next human tap [0500 point 3; 0220 L1 — the promise must
 * have an entry, and a write that did not land gives the human control back].
 */
import type { HandoffControl } from './handoff.contract';
import { kioskFetch } from './kioskFetch';

/*
 * ⭐ **The venue travels on the CREDENTIAL now, and this call names none** [03-us-kad-6c; decisions 0204,
 * 0221]. It used to send `venueId: PILOT_VENUE_ID`, and KOP-1 still scopes the recruiter queue by venue —
 * what changed is where the venue comes from: the api reads it off the kiosk session cookie `kioskFetch`
 * carries, and its `forbidNonWhitelisted` validation now answers `400 property venueId should not exist` to
 * a request that names one. The silence this file keeps by design [BR-K7] is why that 400 would never have
 * surfaced: the candidate waits, the recruiter's screen stays empty, and neither side sees an error.
 */

const REQUEST_TIMEOUT_MS = 10_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/**
 * What one hand-off came to — never an error state on screen [0046; BR-K7; 0457].
 *
 * `landed` is `true` only when the api answered 2xx (on the first request, or on the resend without a refused
 * control). `false` covers a refusal, a `5xx`, and a request that threw: offline, or the
 * {@link REQUEST_TIMEOUT_MS} timeout. ⚠️ A timeout may still have landed server-side. The caller does not retry
 * on its own: the write is fire-and-forget [0046; BR-K7; 0457], so a retry is a human's tap. A second raise in
 * the same visit joins the visit's entry on the server [KOP-1d AC-1: the key is the server's per-visit subject,
 * never a body field], so that tap adds no row.
 * `entryName` is the entry's display name when the answer carries one.
 */
export interface HandoffOutcome {
  readonly landed: boolean;
  readonly entryName?: string;
}

/** What one `POST /handoff` carries — each key absent rather than empty (see below). */
interface HandoffBody {
  readonly candidateId?: number;
  readonly name?: string;
  readonly control?: HandoffControl;
}

/** One `POST /handoff`, through the credentialed egress, bounded by {@link REQUEST_TIMEOUT_MS}. */
function sendHandoff({ candidateId, name, control }: HandoffBody): Promise<Response> {
  return kioskFetch(`${apiBaseUrl()}/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(candidateId === undefined ? {} : { candidateId }),
      ...(name === undefined || name === '' ? {} : { name }),
      ...(control === undefined ? {} : { control }),
    }),
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });
}

/**
 * `name` is what the candidate typed into the *Need help?* control, and nothing else ever supplies it.
 *
 * **Spread rather than passed through as `name: name`**, like `candidateId` above it and for the same
 * reason: the api's DTO bounds it at `Length(1, 60)`, so a present-and-empty key is a 400 where an absent
 * one is a perfectly good anonymous hand-off. Every other caller — a finished flow, the screens that
 * promise a representative — passes nothing and keeps exactly the body it had.
 *
 * ⭐ `control` is the help control the candidate took [KID-5d] — the CONTROL, never a reason or a state: the api
 * maps it to the hand-off reason and the summon state it writes. Spread the same way, so every caller that
 * names none sends exactly the body it sent before, and its entry reads *ready for recruiter* as before.
 *
 * ⭐ **Resolves to whether the write landed, and the entry's display name when the answer carries one**
 * ({@link HandoffOutcome}). `entryName` is absent on a failure, on an answer without it (an api older than
 * TASK-093), and on anything that is not a non-empty string. A press can JOIN an entry that already holds a
 * name [KOP-1d AC-1; 0453], and the *Need help?* box must show the name the recruiter's row shows [03-us-kid-7
 * O4; ISS-38]. It never rejects.
 */
export async function handOffToRecruiter(
  candidateId?: number,
  name?: string,
  control?: HandoffControl,
): Promise<HandoffOutcome> {
  try {
    const response = await sendHandoff({ candidateId, name, control });

    /*
     * ⭐ **A refused CONTROL must not lose the person** [KID-5d review round 1, M1]. An api that does not know the
     * control yet — this app promoted ahead of it, an api rollback, the next control (KID-5f) — answers `400`,
     * and `summonRepresentative` has already marked the session summoned, so nothing would ever ask again while
     * the screen promises a representative. So the SAME request goes once more WITHOUT the control: a refused
     * request stores nothing (`forbidNonWhitelisted`), and the resend lands a *ready for recruiter* row —
     * degraded, but a person on the queue. ⛔ Only on a `400` and only when a control was named: a `5xx` or a
     * timeout says nothing about the control, and resending it could raise a row twice.
     */
    if (response.status === 400 && control !== undefined) {
      reportRefusedControl('/handoff', response.status);
      const resent = await sendHandoff({ candidateId, name });
      if (!resent.ok) {
        reportRefusedWrite('/handoff', resent.status);
        return NOT_LANDED;
      }
      return await landedWith(resent);
    }

    /*
     * ⭐ **Read, and report — but still never raise.** This function swallowed every answer, which is right
     * for the CANDIDATE and wrong for everyone else: a 400 or a 401 here means the entry never landed, and
     * BR-K7 forbids telling the person at the kiosk, not telling an operator.
     */
    if (!response.ok) {
      reportRefusedWrite('/handoff', response.status);
      return NOT_LANDED;
    }
    return await landedWith(response);
  } catch (error: unknown) {
    /*
     * ⭐ **Silent to the candidate, said to an operator** [0500 point 3; 0220 L1]: offline is a write that never
     * reached the queue; the timeout is NO ANSWER — the row may exist. Either way the kiosk cannot say the entry
     * is there, so it raises the same alert as a refusal, and the timeout's message says to check the queue.
     */
    reportFailedRequest('/handoff', error);
    return NOT_LANDED;
  }
}

const NOT_LANDED: HandoffOutcome = { landed: false };

/** A 2xx answer: the write landed, with the entry's name when the answer carries one. */
async function landedWith(response: Response): Promise<HandoffOutcome> {
  const entryName = await entryNameOf(response);
  return entryName === undefined ? { landed: true } : { landed: true, entryName };
}

/**
 * The answer's `entryName`, parsed rather than trusted (`src/services/CLAUDE.md`): a non-empty string, or
 * `undefined`. ⚠️ Hand-read, like `handoff.contract.ts`, until the api emits its OpenAPI schema. The field
 * name is TASK-093's (`iss-kiosk-api`, in parallel), coded against the brief because its PR body was not
 * written when this was built.
 */
async function entryNameOf(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null || !('entryName' in body)) return undefined;
    const { entryName } = body;
    return typeof entryName === 'string' && entryName.trim() !== '' ? entryName : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Says out loud that the api refused the help CONTROL and the hand-off was re-sent without it — ⛔ **to an
 * OPERATOR, never to the candidate** [KID-5d review round 1, M1]. The entry exists, but it reads *ready for
 * recruiter*, so the app and the api disagree about the control set. ⛔ **No candidate id and no name**: this
 * prints on a shared device's console, the same posture as {@link reportRefusedWrite}.
 */
function reportRefusedControl(path: string, status: number): void {
  console.error({
    msg: 'the api REFUSED the hand-off’s help control — re-sent without it, so the entry reads ready for recruiter',
    alert: 'kiosk_handoff_control_refused',
    path,
    status,
  });
}

/**
 * Says out loud that a queue write was refused — ⛔ **to an OPERATOR, never to the candidate.**
 *
 * BR-K7 forbids holding somebody at an unattended kiosk over a staff-side failure, and this respects that:
 * no screen changes, and the caller learns only that the write did not land (`landed: false`), which re-opens
 * the help controls to the next human tap. What it does not do any more is discard the fact. ⚠️ **The wire shape of this call changed with 03-us-kad-6c** — `venueId` left the body and the venue
 * now travels on the credential — so an api that predates KAD-6a answers `400` and the candidate waits for
 * a recruiter nobody told. ⛔ **No candidate id and no factor**: this prints on a shared device's console.
 */
function reportRefusedWrite(path: string, status: number): void {
  console.error({
    msg: 'a recruiter-queue write was REFUSED — the candidate is waiting and nobody has been told',
    alert: 'kiosk_queue_write_failed',
    path,
    status,
  });
}

/**
 * Says out loud that a queue write never got an answer — offline, or the {@link REQUEST_TIMEOUT_MS} timeout —
 * ⛔ **to an OPERATOR, never to the candidate.** The same alert as {@link reportRefusedWrite}, so one rule catches
 * both. ⚠️ **A timeout is no answer, not a refusal**: the row MAY exist, so its message says to check the queue
 * before re-queuing the person. ⛔ **No candidate id, no name, and not the error object**: only its `name`
 * (`TimeoutError`, `TypeError`), because this prints on a shared device's console.
 */
function reportFailedRequest(path: string, error: unknown): void {
  const name =
    typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string'
      ? error.name
      : 'unknown';
  console.error({
    msg:
      name === 'TimeoutError'
        ? 'a recruiter-queue write TIMED OUT — the entry MAY exist: check the queue before re-queuing the candidate'
        : 'a recruiter-queue write got NO ANSWER — the candidate is waiting and nobody has been told',
    alert: 'kiosk_queue_write_failed',
    path,
    error: name,
  });
}
